/**
 * 機能・仕様のモーダル（会議 FB Q-11）。
 *
 * ★ なぜモーダルが要るか ★
 * 機能チップの説明文は `title` 属性にしか入っておらず、タッチ端末では読む手段が無い。
 * 表示されている文字が「内部クリーン」だけでは、何が良いのか伝わらない。
 * 参考にされた できますよ の商品ページと同じく、タップして中身を読める形にする。
 *
 * ★ ネイティブの <dialog> を使う ★
 * ESC・フォーカストラップ・inert・背景クリックの扱いをブラウザに任せる。
 * 自前のフォーカス管理はアクセシビリティの不具合を生みやすい。
 *
 * ★ JS が読み込まれなくても情報は失われない ★
 * トリガーは Liquid 側で hidden、ここで外す。折りたたみ（details）と
 * モーダルの中身はどちらも同じ metafield から Liquid が描いてあるので、
 * JS が動かない環境では折りたたみだけが残る。
 */

class AiconSpecModal extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-spec-dialog]');
    this.trigger = this.querySelector('[data-spec-open]');
    if (!this.dialog || !this.trigger || typeof this.dialog.showModal !== 'function') return;

    this.trigger.hidden = false;
    this.trigger.addEventListener('click', () => this.open());

    this.querySelector('[data-spec-close]')?.addEventListener('click', () => this.dialog.close());

    // 背景（::backdrop）のクリックで閉じる。dialog 自身が全面を覆うため、
    // 「dialog そのものが押された＝中身の外側」で判定する
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.tabs = [...this.querySelectorAll('[data-spec-tab]')];
    this.panels = [...this.querySelectorAll('[data-spec-panel]')];
    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.select(tab.dataset.specTab));
    }

    // 左右キーでタブを移動する（tablist の標準的な操作）
    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => {
      const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      event.preventDefault();
      const current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
      const next = this.tabs[(current + step + this.tabs.length) % this.tabs.length];
      this.select(next.dataset.specTab);
      next.focus();
    });
  }

  open() {
    // 開くたびに先頭のタブへ戻す。前回の続きから始まると迷子になる
    if (this.tabs?.length) this.select(this.tabs[0].dataset.specTab);
    this.dialog.showModal();
  }

  select(name) {
    for (const tab of this.tabs) {
      const on = tab.dataset.specTab === name;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      // 選択中のタブだけを Tab 順に残す（tablist の標準的な挙動）
      tab.tabIndex = on ? 0 : -1;
    }
    for (const panel of this.panels) {
      panel.hidden = panel.dataset.specPanel !== name;
    }
  }
}

if (!customElements.get('aicon-spec-modal')) {
  customElements.define('aicon-spec-modal', AiconSpecModal);
}

/**
 * セクションの外から開くための受け口（日高さん指摘 2026-08-19）。
 *
 * ★ なぜ document に張るのか ★
 * モーダル本体は商品ページの下のほう（製品仕様セクション）にあるが、開くボタンは
 * 購入ボックス＝ページ最上部にも要る。要素側の querySelector はセクション内しか
 * 見ないので届かない。カレンダーバーの `data-aicon-calendar-entry` と同じ方式で、
 * document に1つだけ listener を張る。
 *
 * ★ JS が動かないときはボタンを出さない ★
 * トリガーは Liquid で hidden にしてあり、ここで外す。押しても何も起きないボタンを
 * 残さないため。モーダルが無いページ（商品ページ以外）では出さない。
 */
if (!window.__aiconSpecEntryBound) {
  window.__aiconSpecEntryBound = true;

  const revealEntries = () => {
    if (!document.querySelector('aicon-spec-modal')) return;
    for (const el of document.querySelectorAll('[data-aicon-spec-entry]')) el.hidden = false;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealEntries, { once: true });
  } else {
    revealEntries();
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('[data-aicon-spec-entry]')) return;

    const modal = document.querySelector('aicon-spec-modal');
    if (!modal || typeof modal.open !== 'function') return; // 未 upgrade なら何もしない
    event.preventDefault();
    modal.open();
  });
}
